// src/services/bookingFieldConfiguration.js
import { createFieldSchema } from './fieldConfigurationService';
import { FIELD_TYPES } from '../components/shared/forms/FieldTypes';

export const BOOKING_SERVICE_TYPES = {
  APPOINTMENT: 'appointment',
  CLASS: 'class',
  CONSULTATION: 'consultation',
  FACILITY: 'facility',
  EVENT: 'event',
  WORKSHOP: 'workshop'
};

export const BOOKING_FIELD_CONFIGS = {
  // Core booking fields
  core: [
    createFieldSchema({
      name: 'service',
      type: FIELD_TYPES.SELECT,
      label: 'Service',
      required: true,
      description: 'What would you like to book?'
    }),
    
    createFieldSchema({
      name: 'date',
      type: FIELD_TYPES.DATE,
      label: 'Preferred Date',
      required: true,
      validation: { 
        minDate: new Date().toISOString().split('T')[0] // No past dates
      }
    }),
    
    createFieldSchema({
      name: 'time',
      type: FIELD_TYPES.SELECT,
      label: 'Available Times',
      required: true,
      description: 'Available slots will be shown based on your selected date'
    }),
    
    createFieldSchema({
      name: 'duration',
      type: FIELD_TYPES.SELECT,
      label: 'Duration',
      options: [
        { value: '30', label: '30 minutes' },
        { value: '60', label: '1 hour' },
        { value: '90', label: '1.5 hours' },
        { value: '120', label: '2 hours' }
      ]
    })
  ],

  // Customer information
  customer: [
    createFieldSchema({
      name: 'customerInfo',
      type: FIELD_TYPES.OBJECT,
      label: 'Your Information',
      required: true,
      objectTemplate: {
        firstName: { type: FIELD_TYPES.TEXT, label: 'First Name', required: true },
        lastName: { type: FIELD_TYPES.TEXT, label: 'Last Name', required: true },
        email: { type: FIELD_TYPES.EMAIL, label: 'Email Address', required: true },
        phone: { type: FIELD_TYPES.PHONE, label: 'Phone Number', required: true },
        organization: { type: FIELD_TYPES.TEXT, label: 'Organization (optional)' }
      }
    }),
    
    createFieldSchema({
      name: 'specialRequests',
      type: FIELD_TYPES.TEXTAREA,
      label: 'Special Requests or Notes',
      placeholder: 'Any special requirements, accessibility needs, or additional information...',
      rows: 4
    })
  ],

  // Service-specific configurations
  appointment: [
    createFieldSchema({
      name: 'appointmentType',
      type: FIELD_TYPES.SELECT,
      label: 'Appointment Type',
      options: [
        { value: 'initial', label: '📋 Initial Consultation' },
        { value: 'followup', label: '🔄 Follow-up Appointment' },
        { value: 'urgent', label: '🚨 Urgent Appointment' }
      ]
    }),
    
    createFieldSchema({
      name: 'preferredStaff',
      type: FIELD_TYPES.SELECT,
      label: 'Preferred Staff Member',
      description: 'Select a specific staff member or choose "Any available"'
    }),
    
    createFieldSchema({
      name: 'reasonForVisit',
      type: FIELD_TYPES.TEXTAREA,
      label: 'Reason for Appointment',
      placeholder: 'Please describe the reason for your appointment...',
      required: true
    })
  ],

  consultation: [
    createFieldSchema({
      name: 'consultationType',
      type: FIELD_TYPES.SELECT,
      label: 'Consultation Type',
      options: [
        { value: 'strategy', label: '💡 Strategy Session' },
        { value: 'assessment', label: '📊 Assessment' },
        { value: 'planning', label: '📋 Planning Session' }
      ]
    }),
    
    createFieldSchema({
      name: 'meetingFormat',
      type: FIELD_TYPES.SELECT,
      label: 'Meeting Format',
      options: [
        { value: 'inperson', label: '🏢 In-Person' },
        { value: 'video', label: '💻 Video Call' },
        { value: 'phone', label: '📞 Phone Call' }
      ]
    }),
    
    createFieldSchema({
      name: 'currentChallenges',
      type: FIELD_TYPES.TEXTAREA,
      label: 'Current Challenges',
      placeholder: 'What challenges are you facing that we can help with?',
      rows: 4
    }),
    
    createFieldSchema({
      name: 'goals',
      type: FIELD_TYPES.TEXTAREA,
      label: 'Goals & Objectives',
      placeholder: 'What do you hope to achieve from this consultation?',
      rows: 3
    })
  ],

  class: [
    createFieldSchema({
      name: 'participantCount',
      type: FIELD_TYPES.NUMBER,
      label: 'Number of Participants',
      min: 1,
      max: 50,
      required: true
    }),
    
    createFieldSchema({
      name: 'skillLevel',
      type: FIELD_TYPES.SELECT,
      label: 'Skill Level',
      options: [
        { value: 'beginner', label: '🟢 Beginner' },
        { value: 'intermediate', label: '🟡 Intermediate' },
        { value: 'advanced', label: '🔴 Advanced' },
        { value: 'mixed', label: '🎨 Mixed Levels' }
      ]
    }),
    
    createFieldSchema({
      name: 'equipment',
      type: FIELD_TYPES.MULTISELECT,
      label: 'Equipment Needed',
      options: [
        { value: 'projector', label: 'Projector' },
        { value: 'whiteboard', label: 'Whiteboard' },
        { value: 'computers', label: 'Computers' },
        { value: 'materials', label: 'Course Materials' }
      ]
    })
  ],

  facility: [
    createFieldSchema({
      name: 'facilityType',
      type: FIELD_TYPES.SELECT,
      label: 'Facility Type',
      options: [
        { value: 'meeting_room', label: '🏢 Meeting Room' },
        { value: 'conference_hall', label: '🎪 Conference Hall' },
        { value: 'classroom', label: '🎓 Classroom' },
        { value: 'auditorium', label: '🎭 Auditorium' },
        { value: 'sports', label: '⚽ Sports Facility' }
      ]
    }),
    
    createFieldSchema({
      name: 'expectedAttendees',
      type: FIELD_TYPES.NUMBER,
      label: 'Expected Number of Attendees',
      min: 1,
      max: 1000,
      required: true
    }),
    
    createFieldSchema({
      name: 'setupRequirements',
      type: FIELD_TYPES.MULTISELECT,
      label: 'Setup Requirements',
      options: [
        { value: 'theater', label: 'Theater Style' },
        { value: 'classroom', label: 'Classroom Style' },
        { value: 'roundtables', label: 'Round Tables' },
        { value: 'reception', label: 'Reception Style' },
        { value: 'conference', label: 'Conference Setup' }
      ]
    }),
    
    createFieldSchema({
      name: 'catering',
      type: FIELD_TYPES.SELECT,
      label: 'Catering Requirements',
      options: [
        { value: 'none', label: 'No Catering' },
        { value: 'coffee', label: '☕ Coffee & Tea' },
        { value: 'snacks', label: '🍪 Light Refreshments' },
        { value: 'lunch', label: '🍽️ Full Meal Service' }
      ]
    }),
    
    createFieldSchema({
      name: 'eventDetails',
      type: FIELD_TYPES.TEXTAREA,
      label: 'Event Details',
      placeholder: 'Please provide details about your event, timeline, and any special requirements...',
      rows: 4
    })
  ],

  // Payment and policies
  payment: [
    createFieldSchema({
      name: 'paymentMethod',
      type: FIELD_TYPES.SELECT,
      label: 'Payment Method',
      options: [
        { value: 'card', label: '💳 Credit/Debit Card' },
        { value: 'bank', label: '🏦 Bank Transfer' },
        { value: 'invoice', label: '📄 Invoice (Pay Later)' },
        { value: 'free', label: '🆓 Free Service' }
      ]
    }),
    
    createFieldSchema({
      name: 'cancellationPolicy',
      type: FIELD_TYPES.TOGGLE,
      label: 'I understand the cancellation policy',
      description: '24-hour notice required for cancellations. Late cancellations may incur charges.',
      required: true
    }),
    
    createFieldSchema({
      name: 'termsAccepted',
      type: FIELD_TYPES.TOGGLE,
      label: 'I agree to the terms and conditions',
      required: true
    })
  ]
};

// Get combined field configuration for booking type
export const getBookingFieldConfig = (serviceType = 'appointment') => {
  const coreFields = BOOKING_FIELD_CONFIGS.core;
  const customerFields = BOOKING_FIELD_CONFIGS.customer;
  const serviceFields = BOOKING_FIELD_CONFIGS[serviceType] || [];
  const paymentFields = BOOKING_FIELD_CONFIGS.payment;
  
  return {
    steps: [
      {
        id: 'service',
        title: 'Select Service',
        description: 'Choose what you\'d like to book',
        fields: coreFields
      },
      {
        id: 'details',
        title: 'Service Details',
        description: 'Provide specific details',
        fields: serviceFields
      },
      {
        id: 'customer',
        title: 'Your Information',
        description: 'Tell us about yourself',
        fields: customerFields
      },
      {
        id: 'payment',
        title: 'Confirmation',
        description: 'Review and confirm your booking',
        fields: paymentFields
      }
    ],
    allFields: [...coreFields, ...serviceFields, ...customerFields, ...paymentFields]
  };
};

